import React, { useState, useRef, useEffect } from "react";
import { MoreHorizontal, FileText, Copy } from "lucide-react";
import useTitanApp from "../../../../../hooks/useTitanApp";
import generateSummary from "../../../../../utils/SummaryCreation";
import { toast } from "react-toastify";
import CustomToast from "../../../../controls/CustomToast";
import {
  getAuthHeader,
  refreshAuthToken,
} from "../../../../../services/authToken";
import config from "../../../../../config";
import { generateSummaryReportPdfUrl } from "../../../../../apiUrls/apiUrls";
import { getPharmacyContactDetails } from "@/dispatchers/pharmacyServiceDispatchers";
import { isPathwayAuditJson, formatAuditForPdf } from "@/utils/formatAuditForPdf";

export default function DropdownMenu({ isDarkMode, streamedText,isSummaryGenerating }) {
  const { appState, patientState, pharmacyServiceState } = useTitanApp();
  const {
    darkMode,
    companyId,
    currentCompanyId,
    currentPharmacy,
    currentPharmacyId,
  } = appState || {};
  const {
    demDxSummary,
    demDxSummaryText,
    selectedPatientConsultation,
    selectedPatientDetails,
    selectedPatient,
  } = patientState;
  const { pharmacyContactDetails } = pharmacyServiceState || {};
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDob = (dob: string | null | undefined): string => {
    if (!dob || dob === "1900-01-01T00:00:00") return "";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(dob));
    } catch {
      return "";
    }
  };

  const formatDate = (date: string | null | undefined): string => {
    if (!date) return "";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(date));
    } catch {
      return "";
    }
  };

  const formatSex = (sex: number | null | undefined): string => {
    if (sex === 1) return "Male";
    if (sex === 2) return "Female";
    if (sex === 3) return "Other";
    return "";
  };

  const handleExportPDF = async () => {
    if(isSummaryGenerating) return;
    const processingToast = toast.loading(
    <CustomToast
      title="Processing Consultation Summary PDF"
      description="Please wait while we generate your consultation summary PDF..."
    />,
    {
      className: "services-toast",
      position: toast.POSITION.BOTTOM_RIGHT,
      theme: darkMode ? "dark" : "light",
    }
  );
    let textToExport =
      demDxSummaryText !== ""
        ? demDxSummaryText
        : streamedText !== ""
          ? streamedText
          : selectedPatientConsultation?.consultationSummary || "";
    const contactDetails = await getPharmacyContactDetails(currentPharmacyId);
    let formattedSummary = textToExport.replace(/\\n/g, "\n").trim();
    if (isPathwayAuditJson(formattedSummary)) {
      try {
        formattedSummary = formatAuditForPdf(formattedSummary);
      } catch {
        // fall through with raw text if formatting fails
      }
    }
    const resolvedCompanyId =
      currentCompanyId && currentCompanyId !== ""
        ? currentCompanyId
        : companyId;

    if (!resolvedCompanyId || !formattedSummary) {
      toast.error(
        <CustomToast
          title="Unable to export PDF"
          description="Company and summary details are required."
        />,
        {
          className: "services-toast",
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
          theme: darkMode ? "dark" : "light",
        },
      );
      return;
    }

    const patientName =
      selectedPatientDetails?.patientName ||
      [
        selectedPatientDetails?.title,
        selectedPatientDetails?.foreName,
        selectedPatientDetails?.surName,
      ]
        .filter(Boolean)
        .join(" ") ||
      selectedPatient ||
      "";

    const serviceName = selectedPatientConsultation?.serviceName || "";

    let clinicianName = selectedPatientConsultation?.createdByName || "";

    if(selectedPatientConsultation?.completedByName !== null && selectedPatientConsultation?.completedByName.trim() !== ""){
      clinicianName = selectedPatientConsultation?.completedByName;
    }

    const payload = {
      companyId: resolvedCompanyId,
      summary: formattedSummary,
      serviceName,
      patientName,
      patientAddress: selectedPatientDetails?.address || "",
      patientDob: formatDob(selectedPatientDetails?.dob),
      nhsNumber: selectedPatientDetails?.nhsNumber || "",
      patientSex: formatSex(selectedPatientDetails?.sex),
      pharmacyName: currentPharmacy?.teamPharmacyName || "",
      pharmacyAddress: contactDetails?.address || "",
      pharmacyPhone: contactDetails?.phoneNumber || "",
      clinicianName: clinicianName || "",
      consultationDate: formatDate(selectedPatientConsultation?.createdOn)
    };

    try {
      let authHeader = getAuthHeader();
      if (!authHeader) {
        await refreshAuthToken();
        authHeader = getAuthHeader();
      }

      if (!authHeader) {
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(
        `${config.localurl}${generateSummaryReportPdfUrl()}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to generate summary report PDF.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("content-disposition");
      const fileNameMatch = contentDisposition?.match(
        /filename\*?=(?:UTF-8''|")?([^\";]+)/i,
      );
      const dateStr = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
        .format(new Date())
        .replace(/\//g, "-");
      const safeService = serviceName
        ? serviceName.replace(/[^a-zA-Z0-9]/g, "_")
        : "Consultation";
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/"/g, ""))
        : `Consultation_Summary_${safeService}_${dateStr}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.update(processingToast, {
      render: (
        <CustomToast
          title="Consultation Summary PDF Generated"
          description="Your Consultation Summary PDF has been generated successfully."
        />
      ),
      type: "success",
      isLoading: false,
      autoClose: 3000,
      className: "services-toast",
    });
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error(
        <CustomToast
          title="Export failed"
          description="Unable to generate the summary PDF right now."
        />,
        {
          className: "services-toast",
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
          theme: darkMode ? "dark" : "light",
        },
      );
    }
  };
  const handleCopiedToast = (title, description) => {
    toast.info(<CustomToast title={title} description={description} />, {
      className: "services-toast",
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 3000,
      theme: darkMode ? "dark" : "light",
    });
  };
  const handleCopy = () => {
    if(isSummaryGenerating) return;
    let textToCopy =
      demDxSummary !== ""
        ? generateSummary(demDxSummary)
        : streamedText !== ""
          ? streamedText
          : selectedPatientConsultation?.consultationSummary || "";

    textToCopy = textToCopy.replace(/\\n/g, "\n");

    navigator.clipboard.writeText(textToCopy);
    handleCopiedToast(
      "Copied to clipboard",
      "Consultation summary has been copied.",
    );
  };

  return (
    <div className="custom-dropdown-menu" ref={menuRef}>
      <button
        className={`custom-dropdown-trigger ${isDarkMode ? "dark" : "light"}`}
        onClick={toggleMenu}
      >
        <MoreHorizontal className="icon" />
      </button>

      {isOpen && (
        <div
          className={`custom-dropdown-content ${isDarkMode ? "dark" : "light"}`}
        >
          <button onClick={handleExportPDF} disabled={isSummaryGenerating} className="custom-dropdown-item">
            <FileText className="icon" />
            Export as PDF
          </button>
          <div
            className={`custom-dropdown-separator ${
              isDarkMode ? "dark" : "light"
            }`}
          />
          <button
            className="custom-dropdown-item"
            onClick={() => {
              handleCopy();
            }}
            disabled={isSummaryGenerating}
          >
            <Copy className="icon" />
            Copy All Text
          </button>
        </div>
      )}
    </div>
  );
}
